"use client"

import WatsonApi from "../../../backends/WatsonApi";
import {useStores} from "../../../stores/useStores";
import {Dispatch, SetStateAction, useEffect, useOptimistic, useRef, useState, useTransition} from "react";
import {
    Badge,
    Box, Button,
    Flex, Grid, Heading,
    IconButton,
    ScrollArea,
    Separator, TextArea,
} from "@radix-ui/themes";
import FontAwesome from "../../utilities/FontAwesome";
import {IChannel} from "./ChannelsList";
import useSWRInfinite from "swr/infinite";
import {Message} from "./Message";
import {IMessage, IMessageAttachment} from "../../modals/ActivityModal/ActivityModal";
import {flushSync} from "react-dom";
import styles from "../styles/Conversation.module.scss";
import NotificationManager from "../../notifications/NotificationManager";
import {FileUploader} from "./FileUploader";
import {AttachmentPreview} from "./AttachmentPreview";


function useScrollBottom() {
    const containerRef = useRef<HTMLDivElement>(null);
    const [isAtBottom, setIsAtBottom] = useState(true);

    function scrollBottom() {
        if (containerRef.current) {
            containerRef.current.scrollTo({
                top: containerRef.current.scrollHeight,
                behavior: "instant",
            })
            setIsAtBottom(true);
        }
    }

    useEffect(() => {
        const mutationObserver = new MutationObserver(() => {
            if (!isAtBottom) {
                requestAnimationFrame(() => {
                    scrollBottom();
                })
            }
        })

        if (containerRef.current) {
            mutationObserver.observe(containerRef.current, {
                childList: true,
                subtree: true,
                attributes: true,
            });
        }

        return () => {
            mutationObserver.disconnect();
        }
    }, [isAtBottom]);

    return {
        containerRef,
        setIsAtBottom,
    }
}


export type IPendingMessageAttachment = IMessageAttachment & {
    save_url: string,
}


export default function Conversation(props: {
    recipients: IUser[],
    setRecipientUsers: Dispatch<SetStateAction<IUser[]>>,
    currentChannel: IChannel | undefined,
    setCurrentChannel: (id: IChannel | undefined) => void,
    allowReplies: boolean,
    setShowConversation: Dispatch<SetStateAction<boolean>>,
    mutateChannels: any,
}) {
    const oldMessagesThreshold = useRef<string>(new Date().toISOString());
    const previousScrollHeight = useRef<number | null>(null);
    const {containerRef: scrollbarRef, setIsAtBottom} = useScrollBottom();
    const textAreaRef = useRef<HTMLTextAreaElement>(null);

    const {organizationStore, userStore, modalStore} = useStores();
    const [inputText, setInputText] = useState("");
    const [attachments, setAttachments] = useState<IPendingMessageAttachment[]>([]);
    const [unreadMessages, setUnreadMessages] = useState<IMessage[]>([]);
    const [optimisticUnreadMessages, setOptimisticUnreadMessages] = useOptimistic<IMessage[], IMessage>(unreadMessages, (state, optimisticMessage) => {
        return [
            ...state,
            optimisticMessage,
        ]
    });
    const [isPending, startTransition] = useTransition();

    function addAttachment(attachment: IPendingMessageAttachment) {
        setAttachments(prev => [...prev, attachment]);
    }
    function deleteAttachment(url: IPendingMessageAttachment["url"]) {
        setAttachments(prev => prev.filter(a => a.url !== url));
    }

    const {
        data: oldData,
        isLoading: isLoadingOldData,
        setSize: setOldDataSize,
        isValidating: isValidatingOldData
    } = useSWRInfinite((pageIndex) => props.currentChannel ? (JSON.stringify({
        page: pageIndex,
        key: props.currentChannel.id + "messages-list",
    })) : null, async (key) => {
        const parsedKey = JSON.parse(key);
        const client = await WatsonApi();
        const nextPage = parsedKey.page + 1;
        const response = await client.apis.message_list.message_list_messages_list({
            message_list_pk: (props.currentChannel as IChannel).id,
            page: nextPage,
            page_size: 12,
            created_at__lte: oldMessagesThreshold.current,
            ordering: "-message__created_at",
        });
        const result = JSON.parse(response.data);
        return {
            messages: result.data.reverse(),
            total: result.meta.total,
        }
    }, {
        revalidateOnFocus: false,
        revalidateOnReconnect: false,
        revalidateIfStale: true,
    })

    const oldMessages: IMessage[] = oldData?.reduce((acc, curr) => [...curr.messages, ...acc], []) || [];
    const totalOldMessages: number = oldData?.[0].total || 0;
    const isMoreOldMessages = !isLoadingOldData && totalOldMessages > oldMessages.length;

    async function fetchNewMessages() {
        const client = await WatsonApi();
        const response = await client.apis.message_list.message_list_messages_list({
            message_list_pk: (props.currentChannel as IChannel).id,
            created_at__gt: oldMessagesThreshold.current,
            read_at__isnull: true,
        });
        const newMessages = JSON.parse(response.data).data;
        if (newMessages.length > 0) {
            startTransition(() => {
                setUnreadMessages(v => [...v, ...newMessages]);
            })

            await client.apis.message_list.message_list_messages_mark_read({
                message_list_pk: (props.currentChannel as IChannel).id,
                created_at__lte: newMessages[newMessages.length - 1].created_at,
            })
        }
    }

    useEffect(() => {
        let interval;
        if (props.currentChannel?.id) {
            interval = setInterval(fetchNewMessages, 5000);
        }
        return () => clearInterval(interval);
    }, [props.currentChannel?.id]);

    const messages: IMessage[] = [...oldMessages, ...optimisticUnreadMessages];

    useEffect(() => {
        if (oldData) {
            // when old data is loaded for the first time, or when new messages arrive, scroll to bottom
            setIsAtBottom(false);
        }
    }, [isLoadingOldData, props.currentChannel?.id]);

    useEffect(() => {
        if (oldData && scrollbarRef.current) {
            if (previousScrollHeight.current) {
                // when older data is loaded, scroll down to the bottom of the new messages - this creates a much less jarring experience
                const currentScrollHeight = scrollbarRef.current.scrollHeight;
                scrollbarRef.current.scrollTo({
                    top: currentScrollHeight - previousScrollHeight.current,
                    behavior: "instant",
                })
            }
            previousScrollHeight.current = scrollbarRef.current.scrollHeight;
        }
    }, [oldData?.length]);

    const handleSend = async () => {
        const inputValue = inputText;

        if (!inputValue) return;


            const q = {
                content: inputValue,
                type: "message",
                attachments: attachments.map(attachment => ({
                    content_type: attachment.content_type,
                    url: attachment.save_url,
                    name: attachment.name,
                })),
            }
            if (props.recipients.length > 0) {
                q["recipient_ids"] = props.recipients.map(user => user.id);
            } else if (props.currentChannel?.id) {
                q["message_list_id"] = props.currentChannel.id;
            }

        if (!q["recipient_ids"] && !q["message_list_id"]) {
            return;
        }

        setOptimisticUnreadMessages({
            id: -1,
            content: `<p>${inputValue}</p>`,
            type: "message",
            branding: userStore.avatar ? {
                type: "image",
                value: userStore.avatar,
                alt: "user avatar",
            } : {
                type: "text",
                value: `${userStore.firstName[0]}${userStore.lastName[0]}`,
            },
            created_at: new Date().toISOString(),
            title: `${userStore.firstName} ${userStore.lastName}`,
            grouping_id: "",
            actionable_link: null,
            attachments,
        })

        flushSync(() => {
            // flush this synchronously so optimistic message shows up immediately
            setIsAtBottom(false);
            setInputText("");
            setAttachments([]);
        })

        try {
            const client = await WatsonApi();

            const response = await client.apis.organizations.organizations_messaging_create({
                organization_pk: organizationStore.organization.id,
                data: q
            });

            if (props.currentChannel) {
                await fetchNewMessages();
            } else {
                const messageListId = JSON.parse(response.data).message_list_id;
                const [channels] = await props.mutateChannels();
                startTransition(async () => {
                    props.setCurrentChannel(channels.find(channel => channel.id === messageListId));
                    setAttachments([]);
                    await fetchNewMessages();
                })
            }
        } catch (e) {
            console.error(e);
            NotificationManager.error("Failed to send message", undefined, 3000);
            setUnreadMessages(v => v);
        }
    }

    async function handleLoadOlder() {
        await setOldDataSize(s => s + 1);
    }

    let headingContent;
    if (props.currentChannel) {
        headingContent = <Heading as={"h3"} size={"3"}>{props.currentChannel.title}</Heading>;
    } else if (props.recipients.length > 0) {
        headingContent = <Heading as={"h3"} size={"3"}>
            To: {props.recipients.map(user => <Box display={"inline-block"} pr={"1"}>
            <Badge size={"3"}>
                {user.fname} {user.lname}<IconButton variant={"ghost"} size={"1"} onClick={() => {
                props.setRecipientUsers(prev => prev.filter(u => u.id !== user.id));
            }}>
                <FontAwesome name={"fa-times"} prefix={"fas"}/>
            </IconButton>
            </Badge>
        </Box>)}
        </Heading>
    } else if (props.recipients.length === 0) {
        headingContent = <Flex asChild align={"center"}>
            <Heading as={"h3"} size={"3"}>
                To:&nbsp;<Button size={"2"} variant={"soft"} onClick={() => {
                modalStore.addModal({
                    type: "AddOrEditMessagingListModal",
                    handleSave: async (listData) => {
                        props.setRecipientUsers(listData.members);
                        props.setCurrentChannel(undefined);
                        modalStore.removeTop();
                    },
                    messageIntent: "message",
                })
            }}>
                Add Recipients
            </Button>
            </Heading>
        </Flex>
    }

    function handleTextChange(e) {
        setInputText(e.target.value);
        if (textAreaRef.current) {
            textAreaRef.current.style.height = "auto";
            const newScrollHeight = Math.min(textAreaRef.current.scrollHeight, 200);
            textAreaRef.current.style.height = newScrollHeight + "px";
        }
    }

    return <Box
        flexBasis={{
            initial: "100%",
            md: "50%",
        }}
        height={"500px"}
        style={{boxShadow: "var(--shadow-6)", backgroundColor: "var(--gray-2)"}}
    >
        <Grid rows={"3"} height={"100%"} style={{gridTemplateRows: "auto 1fr auto"}}>
            <Flex p={"2"} justify={"between"} align={"center"} style={{borderBottom: "1px solid var(--gray-6"}}>
                <Box>
                    {headingContent}
                </Box>
                <IconButton variant={"ghost"} onClick={() => {
                    props.setShowConversation(false);
                    props.setRecipientUsers([]);
                    props.setCurrentChannel(undefined);
                }}>
                    <FontAwesome name={"fa-times"} prefix={"fas"}/>
                </IconButton>
            </Flex>
            <ScrollArea ref={scrollbarRef} scrollbars={"vertical"}>
                <Box px={"2"}>
                    {isMoreOldMessages && <Box pt={"3"} style={{textAlign: "center"}}>
                        <Button variant={"ghost"} onClick={handleLoadOlder} loading={isValidatingOldData}>
                            Load Older
                        </Button>
                        <Separator my={"3"} mx={"auto"} orientation={"horizontal"} size={"2"}/>
                    </Box>}
                    {messages.map(msg => <Message key={msg.id} message={msg}/>)}
                </Box>
            </ScrollArea>
            {props.allowReplies && <Box p={"2"} pt={"0"}>
                <Separator my={"3"} orientation={"horizontal"} size={"4"}/>
                <Flex gap={"3"} align={"center"}>
                    <FileUploader addAttachment={addAttachment} />
                    <Box width={"100%"}>
                        <Flex align={"center"} gapX={"2"} asChild>
                            <form action={handleSend}>
                                <TextArea
                                    ref={textAreaRef}
                                    onChange={handleTextChange}
                                    className={styles.textInput}
                                    size={"3"}
                                    rows={1}
                                    wrap={"soft"}
                                    placeholder={"Type message here"}
                                    value={inputText}
                                />
                                <Button variant={"soft"} disabled={!inputText}>
                                    Send
                                </Button>
                            </form>
                        </Flex>
                        {attachments.length > 0 && <Box mt={"2"}>
                            <Flex gap={"2"} wrap={"wrap"}>
                                {attachments.map(attachment => <AttachmentPreview
                                    key={attachment.url}
                                    attachment={attachment}
                                    deleteAttachment={deleteAttachment}
                                />)}
                            </Flex>
                        </Box>}
                    </Box>
                </Flex>
            </Box>}
        </Grid>
    </Box>;
}